import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightLeft,
  Bug,
  CalendarCheck,
  FileSearch,
  ListChecks,
  Loader2,
  MessageCircleQuestion,
  MessagesSquare,
  PenLine,
  Send,
  Settings2,
  UserRoundX,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import type { ToolsetId } from "@ttc-spectree/toolbox-contract";
import { Chip, type ChipTone } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import { useMe } from "@/hooks/queries/use-me";
import {
  authorsDetail,
  authorsTitle,
  briefsTitle,
  claudeDetail,
  claudeTitle,
  roleWordsOn,
  sessionDetail,
  sessionTitle,
  useNextUp,
  useNextUpProject,
  type NextUp,
} from "@/hooks/queries/use-next-up";
import { canAnywhere, canOn, hasNoRole, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { usePageTitle } from "@/hooks/use-page-title";
import { GROOMING_QUEUE_PATH, groomingPath } from "@/lib/grooming/paths";
import { useToolboxBridge, useToolsetSupport, type ToolsetSupport } from "@/lib/toolbox/bridge";
import {
  ASK_PATH,
  ASK_TOOL_NAME,
  FOGBUGZ_FORMULATOR_PATH,
  FOGBUGZ_FORMULATOR_TOOL_NAME,
  FOGBUGZ_PATH,
  FOGBUGZ_TO_ADO_PATH,
  FOGBUGZ_TO_ADO_TOOL_DESCRIPTION,
  FOGBUGZ_TO_ADO_TOOL_NAME,
  FOGBUGZ_TOOL_NAME,
  ROLES_PATH,
  fogbugzToAdoVisible,
} from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

interface ToolsetInfo {
  id: ToolsetId;
  name: string;
  description: string;
  href: string;
  icon: LucideIcon;
}

/**
 * Every toolset, in display order. Each shows only when the person's Toolbox
 * roles allow it somewhere (epic PI-SPEC-SW-E283): Grooming with
 * `read-backlog` on a project, the Formulator with `formulator` on a project,
 * the FogBugz Formulator (epic PI-SPEC-SW-E309, ER-1665 Resolved Decision 3)
 * and the FogBugz event log summary (epic PI-SPEC-SW-E307, ER-1663 Resolved
 * Decision 3) with `fogbugz` on a project, Product Q&A (epic PI-SPEC-SW-E310,
 * ER-1667 Package F) with `ask` on a project, FogBugz to ADO (epic
 * PI-SPEC-SW-E311, ER-1666 Package E) with `fogbugz-to-ado` on a project and
 * a saved FogBugz key, in an app that ships the toolset.
 */
const TOOLSETS: readonly ToolsetInfo[] = [
  {
    id: "grooming",
    name: "Grooming",
    description: "Prepare backlog items for the grooming session with a plain-language brief and a suggested size.",
    href: "/grooming",
    icon: ListChecks,
  },
  {
    id: "formulator",
    name: "ADO Formulator",
    description:
      "Write an Azure DevOps user story or bug that meets the grooming bar. Claude reads the code first, then asks what is missing.",
    href: "/toolbox/formulator",
    icon: PenLine,
  },
  {
    id: "fogbugz-formulator",
    name: FOGBUGZ_FORMULATOR_TOOL_NAME,
    description:
      "Report a bug or ask for a feature in FogBugz. Claude reads the product code first, then asks what is missing.",
    href: FOGBUGZ_FORMULATOR_PATH,
    icon: Bug,
  },
  {
    id: "fogbugz",
    name: FOGBUGZ_TOOL_NAME,
    description: "Summarize an event log (.evtx) on a FogBugz case with your Claude, review it, then post it to the case.",
    href: FOGBUGZ_PATH,
    icon: FileSearch,
  },
  {
    id: "fogbugz-to-ado",
    name: FOGBUGZ_TO_ADO_TOOL_NAME,
    description: FOGBUGZ_TO_ADO_TOOL_DESCRIPTION,
    href: FOGBUGZ_TO_ADO_PATH,
    icon: ArrowRightLeft,
  },
  {
    id: "ask",
    name: ASK_TOOL_NAME,
    description:
      "Ask how the product behaves. Your Claude reads the product's code and answers in plain words, saying whether a written spec backs it.",
    href: ASK_PATH,
    icon: MessagesSquare,
  },
];

/** What the person may use anywhere, one flag per tool tile. */
export interface ToolsetCapabilities {
  /** `read-backlog` on some project: the Grooming tile. */
  groom: boolean;
  /** `formulator` on some project: the ADO Formulator tile. */
  formulate: boolean;
  /** `fogbugz` on some project: the FogBugz Formulator and FogBugz event log summary tiles. */
  fogbugz: boolean;
  /** `ask` on some project: the Product Q&A tile. */
  ask: boolean;
  /**
   * `fogbugz-to-ado` on some project AND a saved FogBugz key AND an app that
   * ships the toolset: the FogBugz to ADO tile (epic PI-SPEC-SW-E311). Absent
   * means hidden.
   */
  fogbugzToAdo?: boolean;
}


/** The toolsets this person sees, from their capabilities anywhere. Pure; exported for tests. */
export function visibleToolsets(capabilities: ToolsetCapabilities): readonly ToolsetInfo[] {
  return TOOLSETS.filter((toolset) => {
    switch (toolset.id) {
      case "grooming":
        return capabilities.groom;
      case "formulator":
        return capabilities.formulate;
      case "fogbugz":
      case "fogbugz-formulator":
        return capabilities.fogbugz;
      case "ask":
        return capabilities.ask;
      case "fogbugz-to-ado":
        return capabilities.fogbugzToAdo === true;
      default:
        return false;
    }
  });
}

/**
 * What a tile needs saved in Settings before it opens: an ADO PAT for
 * Grooming, the ADO Formulator and FogBugz to ADO (it creates the Bug as
 * them; its FogBugz key is already part of whether the tile shows), the
 * person's own FogBugz key for the FogBugz tools (they read, post and file
 * cases as them). Product Q&A needs
 * nothing beyond the role: its page checks each product's code on this
 * computer when the person picks it.
 */
interface TilePrerequisite {
  met: boolean;
  /** The chip shown instead of Open while unmet. */
  chip: string;
  testId: string;
}

/** The tile prerequisite for `toolset`. Pure; exported for tests. */
export function tilePrerequisite(
  toolset: ToolsetId,
  saved: { hasAdoPat: boolean; hasFogbugzKey: boolean },
): TilePrerequisite {
  if (toolset === "ask") return { met: true, chip: "", testId: `toolset-needs-nothing-${toolset}` };
  if (toolset === "fogbugz" || toolset === "fogbugz-formulator") {
    return { met: saved.hasFogbugzKey, chip: "Save your FogBugz key", testId: `toolset-needs-key-${toolset}` };
  }
  return { met: saved.hasAdoPat, chip: "Save an ADO PAT in Settings", testId: `toolset-needs-pat-${toolset}` };
}

/** Shown instead of the tiles to someone with no Toolbox role on any project. */
function NoRoleCard() {
  return (
    <div className="glass-card flex items-start gap-3 rounded-[10px] px-[18px] py-4" data-testid="toolbox-no-role">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1A1A1E]" aria-hidden="true">
        <UserRoundX className="h-4 w-4 text-muted-foreground" />
      </span>
      <div className="flex flex-col gap-1">
        <h2 className="font-display text-base font-semibold leading-snug">No Toolbox role yet</h2>
        <p className="leading-relaxed text-muted-foreground">Ask an Admin or your Project Manager for a role.</p>
      </div>
    </div>
  );
}

/** The support chip of a toolset tile; the Formulator page shows the same words when it cannot run. */
export const SUPPORT_LABELS: Record<Exclude<ToolsetSupport, "browser">, { text: string; tone: ChipTone }> = {
  supported: { text: "Ready", tone: "ok" },
  "app-update-needed": { text: "Update SpecTree Toolbox to use this", tone: "warn" },
  "spectree-update-needed": { text: "Needs a newer SpecTree; ask your SpecTree admin", tone: "warn" },
};

function SupportChip({ support }: { support: ToolsetSupport | undefined }) {
  if (support === undefined || support === "browser") {
    return <Chip tone="none">Checking…</Chip>;
  }
  const label = SUPPORT_LABELS[support];
  return (
    <Chip tone={label.tone} testId="toolset-support">
      {label.text}
    </Chip>
  );
}

/**
 * One tool as a compact card (frame 1f): icon, name, a one-line description
 * and the support chip. The whole card opens the tool once it is supported
 * and its prerequisite is saved (an ADO PAT, or the FogBugz key for the
 * FogBugz tool); without it the card points to Settings instead.
 */
function ToolsetTile({ toolset, prerequisite }: { toolset: ToolsetInfo; prerequisite: TilePrerequisite }) {
  const support = useToolsetSupport(toolset.id);
  const Icon = toolset.icon;
  const openable = support === "supported" && prerequisite.met;
  const row = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1A1A1E]" aria-hidden="true">
        <Icon className="h-4 w-4 text-primary" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-display text-sm font-semibold leading-snug">{toolset.name}</span>
        <span className="truncate text-xs text-muted-foreground" title={toolset.description}>
          {toolset.description}
        </span>
      </span>
      <SupportChip support={support} />
    </>
  );
  return (
    <div
      className="glass-card flex flex-col gap-2 rounded-[10px] transition-colors hover:border-white/[0.12]"
      data-testid={`toolset-tile-${toolset.id}`}
    >
      {openable ? (
        <Link
          to={toolset.href}
          aria-label={`Open ${toolset.name}`}
          className="flex items-center gap-3 rounded-[10px] px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`toolset-open-${toolset.id}`}
        >
          {row}
        </Link>
      ) : (
        <div className="flex items-center gap-3 px-4 py-3">{row}</div>
      )}
      {support === "supported" && !prerequisite.met && (
        <div className="flex items-center justify-end gap-2 px-4 pb-3" data-testid={prerequisite.testId}>
          <Chip tone="warn">{prerequisite.chip}</Chip>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings">Settings</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

/** "Good morning" before noon, "Good afternoon" before 6 pm, else "Good evening" (local time). Pure; exported for tests. */
export function greetingFor(now: Date = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/** One Next up row (frame 1f `.qcard`): icon, two lines of text, one button. */
function NextUpRow({
  testId,
  icon,
  title,
  detail,
  action,
}: {
  testId: string;
  icon: ReactNode;
  title: string;
  detail: string;
  action: ReactNode;
}) {
  return (
    <li className="flex items-center gap-3.5 px-4 py-3.5" data-testid={testId}>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold leading-snug">{title}</span>
        <span className="truncate text-xs text-muted-foreground">{detail}</span>
      </span>
      {action}
    </li>
  );
}

/** Which Next up rows the person may act on, from their capabilities on the project. */
interface NextUpGates {
  /** `manage-agenda`: posting the session's briefs (Review). */
  briefs: boolean;
  /** `post-single-brief`: asking authors (Open Backlog). */
  authors: boolean;
}

/**
 * Toolbox home's Next up (UX-PM-33, frame 1f): the next session, briefs ready
 * to post, items waiting on their authors and, in the app, what Claude is
 * preparing. Only lines with something to do are shown, each with one
 * button. A hint only: nothing here blocks anything.
 */
function NextUpCard({ project, nextUp, gates }: { project: string; nextUp: NextUp; gates: NextUpGates }) {
  const rows: ReactNode[] = [];
  if (nextUp.session) {
    rows.push(
      <NextUpRow
        key="session"
        testId="next-up-session"
        icon={<CalendarCheck className="h-5 w-5 text-[#FF6B81]" />}
        title={sessionTitle(nextUp.session)}
        detail={sessionDetail(nextUp.session)}
        action={
          <Button asChild size="sm" className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            <Link to={groomingPath(project, "session")}>Open session</Link>
          </Button>
        }
      />,
    );
  }
  if (nextUp.briefsToPost !== undefined && gates.briefs) {
    rows.push(
      <NextUpRow
        key="briefs"
        testId="next-up-briefs"
        icon={<Send className="h-5 w-5 text-[#6CC4FF]" />}
        title={briefsTitle(nextUp.briefsToPost)}
        detail="Review them before the meeting; the team reads them in ADO"
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={`${groomingPath(project, "backlog")}?stage=ready-to-groom`}>Review</Link>
          </Button>
        }
      />,
    );
  }
  if (nextUp.authors && gates.authors) {
    rows.push(
      <NextUpRow
        key="authors"
        testId="next-up-authors"
        icon={<MessageCircleQuestion className="h-5 w-5 text-[#FBBF24]" />}
        title={authorsTitle(nextUp.authors)}
        detail={authorsDetail(nextUp.authors)}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={`${groomingPath(project, "backlog")}?stage=needs-information`}>Open Backlog</Link>
          </Button>
        }
      />,
    );
  }
  if (nextUp.claude) {
    rows.push(
      <NextUpRow
        key="claude"
        testId="next-up-claude"
        icon={<Loader2 className="h-5 w-5 animate-spin text-muted-foreground motion-reduce:animate-none" />}
        title={claudeTitle(nextUp.claude)}
        detail={claudeDetail(nextUp.claude)}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={GROOMING_QUEUE_PATH}>Queue</Link>
          </Button>
        }
      />,
    );
  }
  // While the reads load with nothing to show yet, the whole section stays hidden.
  if (rows.length === 0 && nextUp.loading) return null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="toolbox-next-up" data-testid="toolbox-next-up">
      <h2 id="toolbox-next-up" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">
        Next up
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="next-up-nothing">
          Nothing needs you right now.
        </p>
      ) : (
        <ul className="glass-card flex flex-col divide-y divide-[#18181B] rounded-[10px]">{rows}</ul>
      )}
    </section>
  );
}

/**
 * `/toolbox`: the page SpecTree Toolbox opens. One tile per toolset with its
 * support state; in a plain browser, guidance to open the app instead
 * (epic PI-SPEC-SW-E279, task PI-SPEC-SW-1395-4). Restyled to the v2
 * desktop look in epic PI-SPEC-SW-E280 (task PI-SPEC-SW-1405-1). Since
 * epic PI-SPEC-SW-E283 the tiles follow the person's Toolbox roles: Grooming
 * with `read-backlog` on some project, the Formulator with `formulator` on
 * some project; someone with no role sees "Ask an Admin or your Project
 * Manager for a role". Both ADO tools need an ADO PAT; without one a tile
 * points to Settings instead of offering Open. The FogBugz event log summary
 * (epic PI-SPEC-SW-E307) shows with `fogbugz` on some project and needs the
 * person's saved FogBugz key instead: without it the tile says "Save your
 * FogBugz key" with a Settings button. The FogBugz Formulator (epic
 * PI-SPEC-SW-E309) follows the same two rules. Product Q&A (epic
 * PI-SPEC-SW-E310) shows with `ask` on some project and needs nothing saved.
 * Admins and Project Managers
 * (`assign-roles` somewhere) also get a Roles link to `/toolbox/roles`, in
 * the app and in a browser (feature PI-SPEC-SW-1431).
 *
 * Next up (epic PI-SPEC-SW-E292, feature PI-SPEC-SW-1499; UX-PM-33, frame
 * 1f): for the person's current project (the last grooming project shown on
 * this computer while they may still read it, else the first they may read)
 * the header greets them and says their role there, and a Next up card lists
 * the next session, briefs ready to post, items waiting on their authors and,
 * in the app, what Claude is preparing; only lines with something to do, each
 * with one button. In a browser Next up shows too, without the Claude line.
 * Tools follow as one compact row. Without a project or a role, home is as
 * before: the tiles or the no-role card, and no Next up.
 */
export function ToolboxHomePage() {
  usePageTitle("SpecTree Toolbox");
  const bridge = useToolboxBridge();
  const { data: me } = useMe();
  const { data: access, isLoading: accessLoading } = useToolboxAccess();
  const toolsets = visibleToolsets({
    groom: canAnywhere(access, "read-backlog"),
    formulate: canAnywhere(access, "formulator"),
    fogbugz: canAnywhere(access, "fogbugz"),
    ask: canAnywhere(access, "ask"),
    fogbugzToAdo: fogbugzToAdoVisible(canAnywhere(access, "fogbugz-to-ado"), me?.hasFogbugzKey ?? false, bridge),
  });
  const noRole = hasNoRole(access);
  const mayAssignRoles = canAnywhere(access, "assign-roles");
  const saved = { hasAdoPat: me?.hasAdoPat ?? false, hasFogbugzKey: me?.hasFogbugzKey ?? false };
  const homeProject = useNextUpProject();
  const project = noRole ? undefined : homeProject;
  const nextUp = useNextUp(project);
  const roleWords = roleWordsOn(access, project);
  const gates: NextUpGates = {
    briefs: canOn(access, project, "manage-agenda"),
    authors: canOn(access, project, "post-single-brief"),
  };

  return (
    <div className="flex w-full flex-col gap-5 px-4 pb-6 pt-7">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {project ? (
            <>
              <h1 className={TOOLBOX_PAGE_TITLE} data-testid="toolbox-greeting">
                {greetingFor()}
              </h1>
              <p className="truncate text-[13px] text-muted-foreground" data-testid="toolbox-project-line">
                {roleWords ? `${project} · you are the ${roleWords}` : project}
              </p>
            </>
          ) : (
            <>
              <h1 className={cn("flex items-center gap-2", TOOLBOX_PAGE_TITLE)}>
                <span className="h-2 w-2 rounded-[2px] bg-primary" aria-hidden="true" />
                SpecTree Toolbox
              </h1>
              <p className="text-[13px] text-muted-foreground">
                Tools that run Claude Code on your computer. Results are saved in SpecTree.
              </p>
            </>
          )}
        </div>
        {bridge && (
          <Button asChild variant="outline" size="sm">
            <Link to="/toolbox/setup">
              <Settings2 aria-hidden="true" />
              Setup checks
            </Link>
          </Button>
        )}
        {mayAssignRoles && (
          <Button asChild variant="outline" size="sm">
            <Link to={ROLES_PATH} data-testid="toolbox-roles-link">
              <UsersRound aria-hidden="true" />
              Roles
            </Link>
          </Button>
        )}
      </div>

      {project && <NextUpCard project={project} nextUp={nextUp} gates={gates} />}

      {bridge ? (
        <section className="flex flex-col gap-2" aria-labelledby="toolbox-tools">
          <h2 id="toolbox-tools" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">
            Tools
          </h2>
          {accessLoading ? (
            <p className="text-sm text-muted-foreground" role="status">
              Loading your tools…
            </p>
          ) : noRole || toolsets.length === 0 ? (
            <NoRoleCard />
          ) : (
            <div
              className={cn("grid gap-3.5", toolsets.length > 1 ? "grid-cols-2" : "grid-cols-1")}
              data-testid="toolbox-tool-row"
            >
              {toolsets.map((toolset) => (
                <ToolsetTile key={toolset.id} toolset={toolset} prerequisite={tilePrerequisite(toolset.id, saved)} />
              ))}
            </div>
          )}
        </section>
      ) : (
        <OpenInToolboxCallout />
      )}
    </div>
  );
}
